/* eslint-disable @typescript-eslint/unbound-method */
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HubConnectionState } from '@microsoft/signalr';
import { AbilityService } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import { ButtonComponent, CheckboxComponent, ModalComponent, ModalFooterDirective } from 'mbs-ui-kit';
import {
  GoogleQRCodeComponent,
  InstallMspAppInstructionComponent,
  MspQRCodeComponent,
  RecoveryCodeComponent
} from 'mbs-ui-shared@components';
import { ReserveAuthPanelComponent } from 'mbs-ui-shared@components/reserve-auth-panel/reserve-auth-panel';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { TFAFacade } from 'mbs-ui-shared@facades/tfa.facade';
import { PermissionsEnum, TFAStatus } from 'mbs-ui-shared@models';
import { SignalRService, WSTopicName } from 'mbs-ui-shared@modules';
import { TFAService } from 'mbs-ui-shared@services/tfa.service';

@Component({
  selector: 'mbs-two-factor-enable-modal',
  templateUrl: './two-factor-enable-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    GoogleQRCodeComponent,
    MspQRCodeComponent,
    InstallMspAppInstructionComponent,
    ButtonComponent,
    RecoveryCodeComponent,
    ReactiveFormsModule,
    FormsModule,
    ReserveAuthPanelComponent,
    CheckboxComponent,
    I18NextPipe,
    ModalComponent,
    ModalFooterDirective
  ]
})
export class TwoFactorEnableModalComponent implements OnInit, OnDestroy {
  private signalR = inject(SignalRService);
  private cdr = inject(ChangeDetectorRef);
  private TFAService = inject(TFAService);
  private authFacade = inject(AuthFacade);
  private TFAFacade = inject(TFAFacade);
  private destroyRef = inject(DestroyRef);
  public ability = inject(AbilityService);

  public isGoogleAuth = !this.ability.can('read', PermissionsEnum.ShowMSP360ControlMobileApplication);
  public isValidate = false;
  public isSavedRecoveryCodes = false;
  public codes: Array<string>;
  public showThirdPartyAuthButton = this.ability.can('read', PermissionsEnum.ShowMSP360ControlMobileApplication);
  public authId = null;
  public isReserveAuthProcessCompleted = false;
  public isReserveAuthProcessOngoing = false;
  public isPolling = false;

  public get isFinishAvailable() {
    return this.isSavedRecoveryCodes && (this.isReserveAuthProcessCompleted || !this.isReserveAuthProcessOngoing);
  }

  @ViewChild(ModalComponent, { static: true }) baseModal: ModalComponent;

  ngOnInit(): void {
    this.signalR.authorizedZoneConnection.on(WSTopicName.OnQrUsingWithinTwoFactorAuthOperation, (result) => {
      this.authId = JSON.parse(result).AuthId;
    });

    this.signalR.authorizedZoneConnection.on(WSTopicName.OnTwoFactorAuthProcessCompleted, (result) => {
      this.handleTFAResponse(result, this.authId);
    });
  }

  public onQRCodeSet(authId: string) {
    this.authId = authId;
    this.handleNoConnection(authId);
  }

  handleTFAResponse(result: TFAStatus, authId: string) {
    if (result.resolution === 1 && authId === result.authId) {
      this.TFAService.generateCodes(authId).subscribe((result) => {
        this.authFacade.fetchCurrentUser$.subscribe();
        this.TFAFacade.loadTFAStatus(true);
        this.isValidate = true;
        this.codes = result;
        this.cdr.detectChanges();
      });
    }
    if (result.resolution === 2) {
      this.baseModal.close();
    }
  }

  handleNoConnection(authId: string) {
    if (this.signalR.authorizedZoneConnection.state === HubConnectionState.Connected && this.isPolling) {
      return;
    }

    this.isPolling = true;

    this.TFAFacade.handleTFAPolling(authId, (response) => this.handleTFAResponse(response, authId), this.destroyRef);
  }

  ngOnDestroy() {
    this.signalR.authorizedZoneConnection.off('OnTwoFactorAuthProcessCompleted');
  }
}
